import { FormView, getPreset, isInlinePreset, PresetId } from '@account/account-portal/config'
import { Touchpoint, useRuntimeInfo } from '@account/account-portal/lib'
import { LoginOption, AccountPortalProps, PageContext } from '@account/account-portal/types'
import { ConsentRequestPayload } from '@account/consents-shared-lib'
import { useMfProps } from '@crystal/core'
import { createContext, FC, PropsWithChildren, useContext, useState } from 'react'

import { ScreenViews } from './types'

type TouchPointConsent = {
  payload: ConsentRequestPayload
  isValid?: boolean | undefined
  showErrors?: boolean | undefined
}

export interface ViewData {
  screenId: ScreenViews
  email?: string
  persistentLogin?: boolean
  showPersistentLoginCheckbox?: boolean
  accessToken?: string
  refreshToken?: string
  isInlineView: boolean
  pageContext?: PageContext
  preset?: PresetId
  touchpoint?: Touchpoint
  minAgeConfirmation?: boolean
  touchPointConsent?: TouchPointConsent
  formView?: FormView
  loginMethod?: LoginOption
}

interface ViewDataContextType {
  viewData: ViewData
  updateViewData: (data: Partial<ViewData>) => void
  showScreen: (screenId: ScreenViews) => void
}

const ViewDataContext = createContext<ViewDataContextType>({
  viewData: {
    screenId: ScreenViews.LOADING_SCREEN,
    isInlineView: false,
  },
  updateViewData: () => undefined,
  showScreen: () => undefined,
})

// Default view setup
export const ViewDataProvider: FC<PropsWithChildren<{ preset?: PresetId }>> = ({ children, preset }) => {
  const isAuthProvider = getPreset(preset).isAuthProvider

  const [viewData, setViewData] = useState<ViewData>({
    screenId: ScreenViews.LOADING_SCREEN,
    formView: FormView.TwoStep,
    isInlineView: isInlinePreset(preset),
    persistentLogin: isAuthProvider,
    showPersistentLoginCheckbox: !isAuthProvider,
  })

  const updateViewData = (data: Partial<ViewData>) => {
    setViewData(current => ({
      ...current,
      ...data,
    }))
  }

  const showScreen = (screenId: ScreenViews) => {
    updateViewData({ screenId })
  }

  return (
    <ViewDataContext.Provider
      value={{
        viewData,
        updateViewData,
        showScreen,
      }}>
      {children}
    </ViewDataContext.Provider>
  )
}

export const useViewData = () => {
  const viewContext = useContext(ViewDataContext)
  const props = useMfProps<AccountPortalProps>()
  const state = useRuntimeInfo()
  const { isInlineView } = viewContext.viewData
  const propsTouchpoint = props.touchpoint || Touchpoint.Unknown
  const stateTouchpoint = state.touchpoint || Touchpoint.Unknown

  const data: ViewDataContextType = {
    ...viewContext,
    viewData: {
      ...viewContext.viewData,
      pageContext: isInlineView ? props.pageContext : state.pageContext,
      preset: isInlineView ? props.preset : state.preset,
      touchpoint: isInlineView ? propsTouchpoint : stateTouchpoint,
    },
  }

  return data
}
